import { useStore, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import React from 'react';

import TimelineContextMenu from './TimelineContextMenu';
import { TimelineScenesTrack } from './TimelineScenesTrack';
import { TimelineTrack } from './TimelineTrack';

import type { ElementState } from '@src/renderer/ElementState';
import type { Story } from '@src/types.ts/story';
import type { TrackType } from '@src/utility/trackTypes';

interface TimelineTracksProps {
  tracks: [number, ElementState[]][];
  activeTracks: number[];
  scale: number;
  trackWidth?: number;
  onContextMenu: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    element: ElementState,
  ) => void;
  onPasteContextMenu: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    track: number,
    elements: ElementState[],
  ) => void;
  onElementClick: (element: ElementState) => void;
  onSplitSceneAtPlayhead: () => boolean;
  contextMenuState: any;
  story?: Story;
  getTrackInfo: (
    track: number,
    elements: ElementState[],
  ) => {
    type: TrackType;
    isActive: boolean;
    height: number;
  };
}

export const TimelineTracks: React.FC<TimelineTracksProps> = ({
  tracks,
  trackWidth,
  onContextMenu,
  onPasteContextMenu,
  onElementClick,
  onSplitSceneAtPlayhead,
  story,
  getTrackInfo,
}) => {
  const { editorStore } = useStore();
  const userIdentity = useUserIdentity();
  const { sceneManipulationLimit } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const timelineScenes = editorStore.editor.timeline.scenes;
  const hasScenes = timelineScenes.length > 0;

  // If sceneManipulationLimit is set (> 0) and scenes exceed the limit, make scenes view-only
  const isScenesViewOnly =
    sceneManipulationLimit > 0 &&
    timelineScenes.length > sceneManipulationLimit &&
    (editorStore.getPreviewLayoutConfig().strategy === 'split' ||
      editorStore.getPreviewLayoutConfig().strategy === 'hybrid');

  return (
    <div id="timeline-draggable-bounds">
      {tracks.map(([track, elements]) => {
        const trackInfo = getTrackInfo(track, elements);
        const { isActive, type: trackType } = trackInfo;
        const isOriginalVideo = trackType === 'original-video';

        if (isOriginalVideo) {
          return (
            <React.Fragment key={track}>
              {hasScenes ? (
                <TimelineScenesTrack
                  key={`${track}-scenes`}
                  track={track}
                  scenes={timelineScenes}
                  isActive={isActive}
                  width={trackWidth}
                  onSplitSceneAtPlayhead={onSplitSceneAtPlayhead}
                  isScenesViewOnly={isScenesViewOnly}
                />
              ) : null}
              <TimelineTrack
                key={`${track}-video`}
                track={track}
                elements={elements}
                story={story}
                isActive={isActive}
                trackType="original-video"
                width={trackWidth}
                onElementCrtlClick={onElementClick}
              />
              <TimelineTrack
                key={`${track}-audio`}
                track={track}
                elements={elements}
                story={story}
                isActive={isActive}
                trackType="linked-audio"
                width={trackWidth}
                onElementCrtlClick={onElementClick}
              />
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={track}>
              <TimelineContextMenu
                action="copy"
                secondaryActions={[
                  'deleteSelectedKeypoint',
                  'clearVolumeKeyPoints',
                ]}
              />
              <TimelineTrack
                key={track}
                track={track}
                elements={elements}
                story={story}
                isActive={isActive}
                onContextMenu={onContextMenu}
                handlePasteContextMenu={onPasteContextMenu}
                trackType={trackType}
                width={trackWidth}
                onElementCrtlClick={onElementClick}
              />
            </React.Fragment>
          );
        }
      })}
    </div>
  );
};
